import { getApiBase, getAuthHeaders, toAbsoluteUrl } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Interface cho thông tin bài viết
 */
export interface Post {
  /** ID duy nhất của bài viết */
  id: number;
  /** Tiêu đề bài viết */
  title: string;
  /** Slug của bài viết (URL-friendly) */
  slug: string;
  /** Meta description cho SEO */
  metaDescription?: string;
  /** Keyword chính */
  primaryKeyword?: string;
  /** Danh sách keywords phụ */
  secondaryKeywords?: string[];
  /** Anchor text */
  anchorText?: string;
  /** Nội dung bài viết */
  content: string;
  /** Ảnh đại diện */
  featuredImage?: string;
  /** ID danh mục */
  categoryId?: number;
  /** Thông tin danh mục */
  category?: {
    /** ID danh mục */
    id: number;
    /** Tên danh mục */
    name: string;
    /** Slug danh mục */
    slug: string;
  };
  /** Danh sách tags */
  tags?: string[];
  /** Danh sách liên kết nội bộ */
  internalLinks?: string[];
  /** Danh sách liên kết bên ngoài */
  externalLinks?: string[];
  /** Trạng thái bài viết */
  status: 'pending' | 'approved' | 'rejected';
  /** Lý do từ chối (nếu bị reject) */
  rejectionReason?: string;
  /** ID tác giả */
  authorId: number;
  /** Thông tin tác giả */
  author?: {
    /** ID tác giả */
    id: number;
    /** Tên đầy đủ */
    fullName?: string;
    /** Email */
    email: string;
    /** Avatar */
    avatar?: string;
  };
  /** Thời gian tạo */
  createdAt: string;
  /** Thời gian cập nhật cuối cùng */
  updatedAt: string;
  /** Thời gian duyệt */
  approvedAt?: string;
  /** ID người duyệt */
  approvedBy?: number;
  /** Thông tin người duyệt */
  approver?: {
    /** ID người duyệt */
    id: number;
    /** Tên đầy đủ */
    fullName?: string;
    /** Email */
    email: string;
  };
  /** Số lượt xem */
  viewCount: number;
  /** Số lượt chia sẻ */
  shareCount?: number;
}

/**
 * Interface cho tham số lấy danh sách bài viết
 */
export interface ListPostsParams {
  /** Trang hiện tại (tùy chọn) */
  page?: number;
  /** Số lượng bài viết mỗi trang (tùy chọn) */
  limit?: number;
}

/**
 * Interface cho phản hồi danh sách bài viết
 */
export interface ListPostsResponse {
  /** Danh sách bài viết */
  posts: Post[];
  /** Tổng số bài viết */
  total: number;
  /** Trang hiện tại */
  page: number;
  /** Số lượng bài viết mỗi trang */
  limit: number;
  /** Tổng số trang */
  totalPages: number;
}

/**
 * Interface cho yêu cầu tạo bài viết mới
 */
export interface CreatePostRequest {
  /** Tiêu đề bài viết */
  title: string;
  /** Slug của bài viết (tùy chọn) */
  slug?: string;
  /** Meta description cho SEO */
  metaDescription?: string;
  /** Keyword chính */
  primaryKeyword?: string;
  /** Danh sách keywords phụ */
  secondaryKeywords?: string[];
  /** Anchor text */
  anchorText?: string;
  /** Nội dung bài viết */
  content: string;
  /** Ảnh đại diện */
  featuredImage?: string;
  /** Tên danh mục */
  category?: string;
  /** ID danh mục */
  categoryId?: number;
  /** Danh sách tags */
  tags?: string[];
  /** Danh sách liên kết nội bộ */
  internalLinks?: string[];
  /** Danh sách liên kết bên ngoài */
  externalLinks?: string[];
  /** Trạng thái bài viết */
  status?: 'pending' | 'approved' | 'rejected';
  /** Xác nhận bỏ qua cảnh báo kiểm duyệt (nếu có) */
  confirmOverride?: boolean;
}

/**
 * Interface cho yêu cầu cập nhật bài viết
 */
export interface UpdatePostRequest {
  /** Tiêu đề bài viết (tùy chọn) */
  title?: string;
  /** Slug của bài viết (tùy chọn) */
  slug?: string;
  /** Meta description cho SEO (tùy chọn) */
  metaDescription?: string;
  /** Keyword chính (tùy chọn) */
  primaryKeyword?: string;
  /** Danh sách keywords phụ (tùy chọn) */
  secondaryKeywords?: string[];
  /** Anchor text (tùy chọn) */
  anchorText?: string;
  /** Nội dung bài viết (tùy chọn) */
  content?: string;
  /** Ảnh đại diện (tùy chọn) */
  featuredImage?: string;
  /** Tên danh mục (tùy chọn) */
  category?: string;
  /** ID danh mục (tùy chọn) */
  categoryId?: number;
  /** Danh sách tags (tùy chọn) */
  tags?: string[];
  /** Danh sách liên kết nội bộ (tùy chọn) */
  internalLinks?: string[];
  /** Danh sách liên kết bên ngoài (tùy chọn) */
  externalLinks?: string[];
  /** Trạng thái bài viết (tùy chọn) */
  status?: 'pending' | 'approved' | 'rejected';
  /** Xác nhận bỏ qua cảnh báo kiểm duyệt (nếu có) */
  confirmOverride?: boolean;
}

/**
 * Interface cho thông tin giới hạn bài viết
 */
export interface PostLimits {
  /** Có thể đăng bài không */
  canPost: boolean;
  /** Lý do không thể đăng bài */
  reason?: string;
  /** Thông tin giới hạn */
  limits: {
    /** Số bài viết đã đăng */
    used: number;
    /** Số bài viết tối đa */
    max: number;
    /** Số bài viết còn lại */
    remaining: number;
  };
  /** Thông tin user */
  user: {
    /** ID user */
    id: number;
    /** Email */
    email: string;
    /** Role */
    role: string;
    /** Số token còn lại */
    tokenBalance: number;
  };
  /** Thông tin gói hiện tại */
  currentPackage: {
    /** Loại gói */
    type: string;
    /** Tên gói */
    name: string;
    /** Số bài viết tối đa */
    maxPosts: number;
    /** Giá gói */
    price: number;
  };
}

/**
 * Service xử lý các chức năng quản lý bài viết
 * Bao gồm tạo, đọc, cập nhật, xóa bài viết
 */
export class PostsService {
  /** URL cơ sở của API */
  private static baseUrl = getApiBase();

  /**
   * Lấy headers xác thực cho các request API
   * @returns Record<string, string> - Headers bao gồm Content-Type và Authorization
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  // ================== POSTS ==================
  
  /**
   * Lấy danh sách bài viết của user hiện tại
   * @param params - Tham số lọc và phân trang
   * @returns Promise<ListPostsResponse> - Danh sách bài viết với thông tin phân trang
   * @throws Error - Khi không thể tải danh sách bài viết
   */
  static async getMyPosts(params: ListPostsParams = {}): Promise<ListPostsResponse> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    
    const response = await apiClient.get(`/posts/my-posts?${query.toString()}`.replace(/\?$/, ''));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy thông tin bài viết theo ID
   * @param id - ID của bài viết
   * @returns Promise<Post> - Thông tin bài viết
   * @throws Error - Khi không thể tải thông tin bài viết
   */
  static async getPostById(id: number): Promise<Post> {
    const response = await apiClient.get(`/posts/${id}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải thông tin bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Tạo bài viết mới
   * @param data - Thông tin bài viết cần tạo
   * @returns Promise<{message: string, post: Post, limits: any}> - Thông tin bài viết vừa tạo và giới hạn
   * @throws Error - Khi tạo bài viết thất bại
   */
  static async createPost(data: CreatePostRequest): Promise<{message: string, post: Post, limits: any}> {
    const response = await apiClient.post('/posts', data);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Tạo bài viết thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Gọi API kiểm duyệt nội dung trước khi submit
   */
  static async moderateContent(payload: { title: string; content: string; imageUrls?: string[] }): Promise<{
    isSafe: boolean;
    severity: 'none' | 'warn' | 'block';
    flaggedTerms: string[];
    message?: string;
  }> {
    const response = await apiClient.post('/posts/moderate', payload);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Kiểm duyệt nội dung thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Cập nhật thông tin bài viết
   * @param id - ID của bài viết cần cập nhật
   * @param data - Thông tin bài viết cần cập nhật
   * @returns Promise<Post> - Thông tin bài viết sau khi cập nhật
   * @throws Error - Khi cập nhật bài viết thất bại
   */
  static async updatePost(id: number, data: UpdatePostRequest): Promise<Post> {
    const response = await apiClient.put(`/posts/${id}`, data);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật bài viết thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    
    return response.json();
  }

  /**
   * Xóa cứng bài viết
   * @param id - ID của bài viết cần xóa
   * @returns Promise<{message: string}> - Thông báo xóa thành công
   * @throws Error - Khi xóa bài viết thất bại
   */
  static async deletePost(id: number): Promise<{message: string}> {
    const response = await apiClient.delete(`/posts/${id}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Xóa bài viết thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Xóa mềm bài viết
   * @param id - ID của bài viết cần xóa mềm
   * @returns Promise<{message: string}> - Thông báo xóa mềm thành công
   * @throws Error - Khi xóa mềm bài viết thất bại
   */
  static async softDeletePost(id: number): Promise<{message: string}> {
    const response = await apiClient.delete(`/posts/${id}/soft`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Xóa mềm bài viết thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Khôi phục bài viết đã bị xóa mềm
   * @param id - ID của bài viết cần khôi phục
   * @returns Promise<{message: string, post: Post}> - Thông báo khôi phục thành công và bài viết
   * @throws Error - Khi khôi phục bài viết thất bại
   */
  static async restorePost(id: number): Promise<{message: string, post: Post}> {
    const response = await apiClient.post(`/posts/${id}/restore`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Khôi phục bài viết thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Xóa mềm nhiều bài viết cùng lúc
   * @param ids - Mảng ID của các bài viết cần xóa mềm
   * @returns Promise<{message: string, deletedCount: number}> - Thông báo xóa mềm thành công và số lượng bài viết đã xóa
   * @throws Error - Khi xóa mềm bài viết thất bại
   */
  static async bulkSoftDeletePosts(ids: number[]): Promise<{message: string, deletedCount: number}> {
    if (!ids || ids.length === 0) {
      throw new Error('Không có bài viết nào để xóa');
    }

    const results = [];
    const errors = [];

    // Xóa từng bài viết một cách tuần tự để tránh lỗi
    for (const id of ids) {
      try {
        await this.softDeletePost(id);
        results.push(id);
      } catch (error) {
        errors.push({ id, error: error instanceof Error ? error.message : 'Lỗi không xác định' });
      }
    }

    if (errors.length > 0) {
      const errorMessage = errors.length === ids.length 
        ? 'Không thể xóa bài viết nào'
        : `Xóa thành công ${results.length}/${ids.length} bài viết. Lỗi: ${errors.map(e => `ID ${e.id}: ${e.error}`).join(', ')}`;
      throw new Error(errorMessage);
    }

    return {
      message: `Đã xóa thành công ${results.length} bài viết`,
      deletedCount: results.length
    };
  }

  /**
   * Kiểm tra giới hạn đăng bài của user
   * @returns Promise<PostLimits> - Thông tin về giới hạn và gói dịch vụ hiện tại
   * @throws Error - Khi không thể kiểm tra giới hạn
   */
  static async checkPostLimits(): Promise<PostLimits> {
    const response = await apiClient.get('/posts/check-limits');
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể kiểm tra giới hạn bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy thống kê bài viết của user
   * @returns Promise<any> - Thống kê chi tiết về gói dịch vụ và sử dụng
   * @throws Error - Khi không thể lấy thống kê
   */
  static async getUserPostStats(): Promise<any> {
    const response = await apiClient.get('/posts/stats');
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể lấy thống kê bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  // ================== PUBLIC POSTS ==================
  
  /**
   * Lấy danh sách bài viết public (không cần đăng nhập)
   * @param params - Tham số lọc và phân trang
   * @returns Promise<ListPostsResponse> - Danh sách bài viết public với thông tin phân trang
   * @throws Error - Khi không thể tải danh sách bài viết
   */
  static async getPublicPosts(params: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    sortBy?: 'date' | 'views';
    authorId?: number;
  } = {}): Promise<ListPostsResponse> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.search) query.append('search', params.search);
    if (params.category) query.append('category', params.category);
    if (params.sortBy) query.append('sortBy', params.sortBy);
    if (params.authorId) query.append('authorId', String(params.authorId));
    
    const response = await fetch(`${this.baseUrl}/public/posts?${query.toString()}`.replace(/\?$/, ''), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy thông tin bài viết public theo ID (không cần đăng nhập)
   * @param id - ID của bài viết
   * @returns Promise<Post> - Thông tin bài viết public
   * @throws Error - Khi không thể tải thông tin bài viết
   */
  static async getPublicPostById(id: number): Promise<Post> {
    const response = await fetch(`${this.baseUrl}/public/posts/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải thông tin bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy thông tin bài viết public và tăng viewCount
   * @param id - ID của bài viết
   * @returns Promise<Post> - Thông tin bài viết public với viewCount đã tăng
   */
  static async getPublicPostByIdWithView(id: number): Promise<Post> {
    const response = await fetch(`${this.baseUrl}/public/posts/${id}/view`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải thông tin bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy thông tin bài viết public theo slug
   * @param slug - Slug của bài viết
   * @returns Promise<Post> - Thông tin bài viết public
   * @throws Error - Khi không thể tải thông tin bài viết
   */
  static async getPublicPostBySlug(slug: string): Promise<Post> {
    const response = await fetch(`${this.baseUrl}/public/posts/slug/${encodeURIComponent(slug)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải thông tin bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy thông tin bài viết public theo slug và tăng viewCount
   * @param slug - Slug của bài viết
   * @returns Promise<Post> - Thông tin bài viết public với viewCount đã tăng
   */
  static async getPublicPostBySlugWithView(slug: string): Promise<Post> {
    const response = await fetch(`${this.baseUrl}/public/posts/slug/${encodeURIComponent(slug)}/view`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải thông tin bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  // ================== ADMIN (Posts) ==================
  /**
   * Lấy thống kê tổng quan cho admin
   */
  static async getAdminStats(): Promise<{ total: number; pending: number; approved: number; rejected: number; deleted: number; }> {
    const response = await apiClient.get('/posts/admin/stats');
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể lấy thống kê admin';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Upload hình ảnh nổi bật cho bài viết
   * @param file - File hình ảnh cần upload
   * @returns Promise<{url: string, message: string}> - URL của hình ảnh đã upload
   * @throws Error - Khi upload thất bại
   */
  static async uploadImage(file: File): Promise<{ url: string; message: string }> {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/posts/upload-image`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Upload hình ảnh thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }

    const result = await response.json();
    // Chuyển đổi đường dẫn tương đối thành URL tuyệt đối
    const absoluteUrl = toAbsoluteUrl(result.url);
    
    return {
      url: absoluteUrl,
      message: result.message || 'Upload hình ảnh thành công',
    };
  }

  /**
   * Lấy danh sách bài viết đã duyệt (admin)
   */
  static async getApprovedPosts(params: ListPostsParams = {}): Promise<ListPostsResponse> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    const response = await apiClient.get(`/posts/admin/approved?${query.toString()}`.replace(/\?$/, ''));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách bài viết đã duyệt';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy danh sách bài viết chờ duyệt (admin)
   */
  static async getPendingPosts(params: ListPostsParams = {}): Promise<ListPostsResponse> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    const response = await apiClient.get(`/posts/admin/pending?${query.toString()}`.replace(/\?$/, ''));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách bài viết chờ duyệt';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy danh sách bài viết bị từ chối (admin)
   */
  static async getRejectedPosts(params: ListPostsParams = {}): Promise<ListPostsResponse> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    const response = await apiClient.get(`/posts/admin/rejected?${query.toString()}`.replace(/\?$/, ''));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách bài viết bị từ chối';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }
}
